<template>
    <div class="login-container">
        <!-- Background Elements -->
        <div class="background-elements">
            <div class="floating-orb orb-1"></div>
            <div class="floating-orb orb-2"></div>
            <div class="floating-orb orb-3"></div>
            <div class="gold-sparkle sparkle-1"></div>
            <div class="gold-sparkle sparkle-2"></div>
            <div class="gold-sparkle sparkle-3"></div>
        </div>

        <!-- Main Login Card -->
        <div class="login-card">
            <!-- Header Section -->
            <div class="login-header">
                <div class="logo-container">
                    <div class="logo-glow"></div>
                    <router-link to="/" class="logo-link">
                        <img src="/favicon.ico" alt="Pageant Logo" class="logo-image" />
                    </router-link>
                    <div class="logo-ring"></div>
                </div>
                <h1 class="login-title">JUDGES PORTAL</h1>
                <p class="login-subtitle">Enter your access code to begin scoring</p>
            </div>

            <!-- Login Form -->
            <form class="login-form" @submit.prevent="handleSubmit">
                <!-- Password Input -->
                <div class="input-group">
                    <label for="password" class="input-label">
                        <i class="bi bi-key-fill"></i>
                        Access Code
                    </label>
                    <div class="input-container">
                        <input
                            :type="passwordFieldType"
                            id="password"
                            v-model="password"
                            class="password-input"
                            placeholder="Enter your secret code"
                            required
                            autocomplete="current-password"
                        />
                        <button
                            type="button"
                            @click="togglePasswordVisibility"
                            class="visibility-toggle"
                            :class="{ 'visible': passwordFieldType === 'text' }"
                        >
                            <i :class="passwordFieldType === 'password' ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                        </button>
                    </div>
                </div>

                <!-- Error Message -->
                <div v-if="errmsg" class="error-message">
                    <i class="bi bi-exclamation-triangle-fill"></i>
                    <span>{{ errmsg }}</span>
                </div>

                <!-- Submit Button -->
                <button
                    type="submit"
                    class="login-button"
                    :disabled="isSubmitting || !password"
                    :class="{ 'loading': isSubmitting }"
                >
                    <span class="button-text" v-if="!isSubmitting">
                        <i class="bi bi-lock-fill"></i>
                        Access Judges Panel
                    </span>
                    <span class="button-loading" v-else>
                        <div class="loading-spinner"></div>
                        Authenticating...
                    </span>
                    <div class="button-glow"></div>
                </button>
            </form>

            <!-- Footer -->
            <div class="login-footer">
                <div class="powered-by">
                    <div class="company-logo">
                        <img src="/favicon.ico" alt="Exodous Logo" />
                    </div>
                    <div class="company-info">
                        <span class="powered-text">Powered By</span>
                        <a href="https://exodoustechsolutions.com" target="_blank" class="company-name">
                            Exodous I.T. Solutions
                        </a>
                        <span class="system-name">Tabulation System</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Security Badge -->
        <div class="security-badge">
            <i class="bi bi-shield-check"></i>
            <span>Secure Judges Access</span>
        </div>
    </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import router from "@/router";

export default {
    name: "LoginPage",
    setup() {
        const username = ref("");
        const password = ref("");
        const passwordFieldType = ref("password");
        const store = useStore();
        const errmsg = ref("");
        const returnURL = router.currentRoute.value.query.return_url || "/";
        const isSubmitting = ref(false);

        const togglePasswordVisibility = () => {
            passwordFieldType.value =
                passwordFieldType.value === "password" ? "text" : "password";
        };

        const handleSubmit = async () => {
            if (isSubmitting.value) return;
            
            try {
                isSubmitting.value = true;
                errmsg.value = "";
                await store.dispatch("auth/login", {
                    password: password.value,
                });
                router.replace(returnURL);
            } catch (error) {
                console.error("Login error:", error);
                errmsg.value = error.error ? error.error.message : "Server is unreachable. Please try again.";
                
                // Shake animation for error
                const button = document.querySelector('.login-button');
                button.classList.add('shake');
                setTimeout(() => button.classList.remove('shake'), 500);
            } finally {
                isSubmitting.value = false;
            }
        };

        return {
            username,
            password,
            passwordFieldType,
            togglePasswordVisibility,
            handleSubmit,
            errmsg,
            isSubmitting,
        };
    },
};
</script>

<style scoped>

/* Main Container */
.login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, var(--dark-bg) 0%, #1a1a1a 50%, #2d1b00 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    position: relative;
    overflow: hidden;
    font-family: 'Poppins', sans-serif;
}

/* Background Elements */
.background-elements {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.floating-orb {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, var(--gold-primary) 0%, transparent 70%);
    opacity: 0.1;
    animation: float 6s ease-in-out infinite;
}

.orb-1 {
    width: 200px;
    height: 200px;
    top: 10%;
    left: 10%;
    animation-delay: 0s;
}

.orb-2 {
    width: 150px;
    height: 150px;
    top: 60%;
    right: 15%;
    animation-delay: 2s;
}

.orb-3 {
    width: 100px;
    height: 100px;
    bottom: 20%;
    left: 20%;
    animation-delay: 4s;
}

.gold-sparkle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: var(--text-gold);
    border-radius: 50%;
    opacity: 0.6;
    animation: sparkle 3s ease-in-out infinite;
}

.sparkle-1 { top: 30%; left: 20%; animation-delay: 0s; }
.sparkle-2 { top: 70%; right: 25%; animation-delay: 1s; }
.sparkle-3 { bottom: 40%; left: 80%; animation-delay: 2s; }

/* Login Card */
.login-card {
    background: rgba(26, 26, 26, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid var(--border-gold);
    border-radius: 20px;
    padding: 40px;
    width: 100%;
    max-width: 440px;
    box-shadow: 
        0 20px 60px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(212, 175, 55, 0.1),
        0 0 50px rgba(212, 175, 55, 0.1);
    position: relative;
    z-index: 2;
}

/* Header Section */
.login-header {
    text-align: center;
    margin-bottom: 40px;
}

.logo-container {
    position: relative;
    width: 140px;
    height: 140px;
    margin: 0 auto 25px;
}

.logo-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 160px;
    height: 160px;
    background: radial-gradient(circle, var(--gold-primary) 0%, transparent 70%);
    opacity: 0.2;
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
}

.logo-link {
    display: block;
    position: relative;
    z-index: 2;
}

.logo-image {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    border: 3px solid var(--border-gold);
    box-shadow: 0 0 30px var(--shadow-gold);
    transition: all 0.3s ease;
}

.logo-image:hover {
    transform: scale(1.05);
    box-shadow: 0 0 40px var(--shadow-gold);
}

.logo-ring {
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border: 2px solid transparent;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    opacity: 0.5;
    animation: rotate 10s linear infinite;
}

.login-title {
    font-size: 2.5rem;
    font-weight: 800;
    background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 50%, var(--gold-primary) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 0 30px rgba(212, 175, 55, 0.5);
    margin-bottom: 8px;
    letter-spacing: 2px;
}

.login-subtitle {
    color: var(--text-muted);
    font-size: 1rem;
    font-weight: 300;
}

/* Login Form */
.login-form {
    margin-bottom: 30px;
}
.input-group {
    margin-bottom: 25px;
    width: 100%;
}

.input-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-light);
    font-weight: 600;
    margin-bottom: 10px;
    font-size: 0.95rem;
    width: 100%;
}

.input-label i {
    color: var(--gold-primary);
}

.input-container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
}

.password-input {
    width: 100%;
    padding: 15px 50px 15px 20px;
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid var(--border-gold);
    border-radius: 12px;
    color: var(--text-light);
    font-size: 1rem;
    font-weight: 500;
    transition: all 0.3s ease;
    outline: none;
    box-sizing: border-box;
}

.password-input:focus {
    border-color: var(--gold-primary);
    background: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 20px var(--shadow-gold);
}

.password-input::placeholder {
    color: var(--text-muted);
}

.visibility-toggle {
    position: absolute;
    right: 15px;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 5px;
    border-radius: 5px;
    transition: all 0.3s ease;
    z-index: 3;
}

.visibility-toggle:hover {
    color: var(--gold-primary);
    background: rgba(212, 175, 55, 0.1);
}

.visibility-toggle.visible {
    color: var(--gold-primary);
}

/* Error Message */
.error-message {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(231, 76, 60, 0.1);
    border: 1px solid rgba(231, 76, 60, 0.3);
    color: var(--error-color);
    padding: 12px 15px;
    border-radius: 10px;
    margin-bottom: 20px;
    font-size: 0.9rem;
    font-weight: 500;
}

.error-message i {
    font-size: 1rem;
}

/* Login Button */
.login-button {
    position: relative;
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border: none;
    border-radius: 12px;
    color: var(--dark-bg);
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
}

.login-button:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.4);
}

.login-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

.button-glow {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
}

.login-button:not(:disabled):hover .button-glow {
    left: 100%;
}

.button-text, .button-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
    z-index: 2;
}

.loading-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid transparent;
    border-top: 2px solid var(--dark-bg);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

/* Footer */
.login-footer {
    border-top: 1px solid var(--border-gold);
    padding-top: 25px;
}

.powered-by {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
}

.company-logo {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid var(--border-gold);
    box-shadow: 0 0 10px var(--shadow-gold);
}

.company-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.company-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.powered-text {
    font-size: 0.75rem;
    color: var(--text-muted);
}

.company-name {
    font-weight: 600;
    color: var(--text-light);
    font-size: 0.9rem;
    text-decoration: none;
    transition: color 0.3s ease;
}

.company-name:hover {
    color: var(--gold-primary);
}

.system-name {
    font-size: 0.75rem;
    color: var(--gold-primary);
}

/* Security Badge */
.security-badge {
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(0, 0, 0, 0.5);
    padding: 8px 15px;
    border-radius: 20px;
    border: 1px solid var(--border-gold);
    color: var(--text-muted);
    font-size: 0.8rem;
    backdrop-filter: blur(10px);
}

.security-badge i {
    color: var(--gold-primary);
}

/* Animations */
@keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
}

@keyframes sparkle {
    0%, 100% { opacity: 0; transform: scale(0); }
    50% { opacity: 1; transform: scale(1); }
}

@keyframes pulse {
    0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 0.3; transform: translate(-50%, -50%) scale(1.1); }
}

@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
}

.shake {
    animation: shake 0.5s ease-in-out;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
    .login-container {
        padding: 15px;
    }

    .login-card {
        padding: 30px 25px;
    }

    .logo-container {
        width: 120px;
        height: 120px;
    }

    .logo-image {
        width: 120px;
        height: 120px;
    }

    .login-title {
        font-size: 2rem;
    }

    .login-subtitle {
        font-size: 0.9rem;
    }

    .password-input {
        padding: 12px 45px 12px 15px;
        font-size: 0.95rem;
    }

    .login-button {
        padding: 14px;
        font-size: 1rem;
    }

    .security-badge {
        bottom: 10px;
        right: 10px;
        font-size: 0.75rem;
    }

    .orb-1, .orb-2, .orb-3 {
        display: none;
    }
}

@media (max-width: 480px) {
    .login-card {
        padding: 25px 20px;
    }

    .logo-container {
        width: 100px;
        height: 100px;
    }

    .logo-image {
        width: 100px;
        height: 100px;
    }

    .login-title {
        font-size: 1.8rem;
    }

    .powered-by {
        flex-direction: column;
        gap: 10px;
    }
}
</style>